// Margin reporting helpers (Phase E). Per-job P&L math + group-by
// aggregation. Shared by MarginReportPage and any future dashboard
// tile that wants to surface a margin number.
//
// Cost basis: the sum of every line's COST side (labor hrs * baseCost,
// materials qty * unitCost, expenses amount, fuel mileage cost). That's
// what we actually paid to do the work. Revenue: sum of non-voided
// bills' grandTotal. Margin = revenue - cost. Margin % = margin /
// revenue. Markup % = margin / cost. Quote variance for quoted jobs =
// quoted amount - actual cost (positive = under, negative = over).

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// Per-job P&L row. Pure function — caller provides the job; no side
// effects. Voided bills excluded from revenue. Tax NOT included in
// revenue (tax is pass-through; contractor doesn't keep it). Pre-tax
// revenue uses bill.subtotal when present, falls back to grandTotal -
// taxes for older bills that don't store subtotal separately.
export const jobPnl = (job) => {
  const c = job.costing || {};
  const laborCost = (c.labor || []).reduce(
    (s, l) => s + num(l.hours) * num(l.baseCost),
    0
  );
  const materialsCost = (c.materials || []).reduce(
    (s, m) => s + num(m.qty) * num(m.unitCost),
    0
  );
  const expensesCost = (c.expenses || []).reduce(
    (s, e) => s + num(e.amount),
    0
  );
  const fuelCost = (c.fuel || []).reduce((s, f) => {
    if (f.mode === "flat") return s + num(f.amount);
    return (
      s + num(f.costPerMile) * num(f.milesOneWay) * 2 * num(f.trips)
    );
  }, 0);
  const totalCost = laborCost + materialsCost + expensesCost + fuelCost;

  // Revenue: sum of non-voided bills' pre-tax subtotal. Fall back to
  // grandTotal - sum(taxes) when subtotal isn't stored.
  const bills = (job.bills || []).filter((b) => !b.voided);
  let revenue = 0;
  for (const b of bills) {
    const sub =
      num(b.subtotal) ||
      Math.max(
        0,
        num(b.grandTotal) -
          (b.taxes || []).reduce((s, t) => s + num(t.amount), 0)
      );
    revenue += sub;
  }
  // Legacy single-finalize jobs have no bills[] entry — revenue comes
  // from costing.quotedAmount or the computed line-item total.
  if (bills.length === 0 && job.billFinalized) {
    if (c.priceMode === "quoted") {
      revenue = num(c.quotedAmount);
    } else {
      // T&M finalized: subtotal = sum of billable line amounts.
      const laborBillable = (c.labor || []).reduce(
        (s, l) => s + num(l.hours) * num(l.rate),
        0
      );
      const materialsBillable = (c.materials || []).reduce(
        (s, m) =>
          s + num(m.qty) * num(m.unitCost) * (1 + num(m.markup) / 100),
        0
      );
      const expensesBillable = (c.expenses || []).reduce(
        (s, e) => s + num(e.amount) * (1 + num(e.markup) / 100),
        0
      );
      const fuelBillable = (c.fuel || []).reduce((s, f) => {
        const lineCost =
          f.mode === "flat"
            ? num(f.amount)
            : num(f.costPerMile) * num(f.milesOneWay) * 2 * num(f.trips);
        return s + lineCost * (1 + num(f.markup) / 100);
      }, 0);
      revenue =
        laborBillable + materialsBillable + expensesBillable + fuelBillable;
    }
  }

  const margin = revenue - totalCost;
  const marginPct = revenue > 0 ? (margin / revenue) * 100 : 0;
  const markupPct = totalCost > 0 ? (margin / totalCost) * 100 : 0;

  // Quote-vs-actual variance — only meaningful for quoted jobs.
  const isQuoted = c.priceMode === "quoted";
  const quoteVariance = isQuoted ? num(c.quotedAmount) - revenue : null;

  return {
    jobId: job.id,
    jobNumber: job.jobNumber || "",
    title: job.title || "",
    customerId: job.customerId || "",
    locationId: job.locationId || "",
    jobType: job.jobType || "",
    revenue,
    totalCost,
    margin,
    marginPct,
    markupPct,
    quoteVariance,
    billsCount: bills.length,
    closedAt: job.closedAt || job.billFinalizedAt || "",
    finalizedAt:
      bills.length > 0
        ? bills[bills.length - 1].finalizedAt
        : job.billFinalizedAt || "",
  };
};

// Group-by helpers. Each takes a list of jobPnl rows + a key extractor
// and returns aggregated rows with the same column set as the raw rows.
export const groupByKey = (rows, keyFn, labelFn) => {
  const map = new Map();
  for (const r of rows) {
    const key = keyFn(r);
    if (key === null || key === undefined) continue;
    if (!map.has(key)) {
      map.set(key, {
        key,
        label: labelFn ? labelFn(key, r) : key,
        revenue: 0,
        totalCost: 0,
        margin: 0,
        jobs: 0,
        rows: [],
      });
    }
    const acc = map.get(key);
    acc.revenue += r.revenue;
    acc.totalCost += r.totalCost;
    acc.margin += r.margin;
    acc.jobs += 1;
    acc.rows.push(r);
  }
  return [...map.values()].map((g) => ({
    ...g,
    marginPct: g.revenue > 0 ? (g.margin / g.revenue) * 100 : 0,
    markupPct: g.totalCost > 0 ? (g.margin / g.totalCost) * 100 : 0,
  }));
};

// Month bucket: YYYY-MM from finalizedAt (or closedAt fallback).
export const monthKey = (row) => {
  const d = row.finalizedAt || row.closedAt;
  if (!d) return null;
  return d.slice(0, 7); // YYYY-MM
};

// Quarter bucket: YYYY-Q1..Q4.
export const quarterKey = (row) => {
  const d = row.finalizedAt || row.closedAt;
  if (!d) return null;
  const [y, m] = d.split("-").map((s) => parseInt(s, 10));
  if (!y || !m) return null;
  const q = Math.ceil(m / 3);
  return `${y}-Q${q}`;
};

// Year bucket: YYYY.
export const yearKey = (row) => {
  const d = row.finalizedAt || row.closedAt;
  if (!d) return null;
  return d.slice(0, 4);
};
